import { useEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useSession } from "../session.tsx";

export const SPONSOR_URL = "https://github.com/sponsors/ehsangazar";

/**
 * Sets the document title and description per route.
 *
 * The app is client-rendered, so a crawler that does not run JavaScript sees
 * only the shell in index.html. Google runs it; most others do not. If organic
 * search ever matters here, prerendering the marketing routes at build time is
 * the fix, and this hook is what those prerendered pages would use.
 */
export function usePageMeta(title: string, description?: string) {
  useEffect(() => {
    document.title = title === "Coding" ? "Coding" : `${title} · Coding`;
    if (!description) return;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", description);
  }, [title, description]);
}

/**
 * Routes that get the whole viewport and no shared chrome.
 *
 * Every drill route, not just /drill: a problem opened by name is the same
 * screen and needs the same width. This was an exact path comparison until
 * /drill/:drillId existed, which dropped a chosen problem into the narrow page
 * shell with a header above it.
 */
export function isBareRoute(pathname: string): boolean {
  return pathname === "/drill" || pathname.startsWith("/drill/");
}

export function Layout() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  // The drill needs the whole viewport and its own chrome, so the shared header
  // gets out of the way there rather than stealing a row of the editor.
  if (isBareRoute(location.pathname)) return <Outlet />;

  return (
    <div className="shell">
      <header className="nav">
        {/* The mark, not just the word. Inline rather than an <img> so it costs
            no request and inherits the header's colour transitions; the wordmark
            stays real text so it is selectable and readable to a screen reader. */}
        <Link to="/" className="brand" aria-label="Coding, home">
          <svg viewBox="0 0 32 32" aria-hidden="true">
            <path d="M18.87 7.16 A9.3 9.3 0 1 0 24.84 13.13" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M23.89 10.18 L27.33 12.84 L22.67 14.36 Z" fill="var(--accent)" />
            <path d="M14.24 12.80l3.20 3.20-3.20 3.20" fill="none" stroke="var(--accent)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Coding</span>
        </Link>
        <nav>
          <NavLink to="/drills">Problems</NavLink>
          <NavLink to="/scoreboard">Scoreboard</NavLink>
          <NavLink to="/how-it-works">How it works</NavLink>
        </nav>
        <div className="nav-right">
          {user ? (
            <>
              <Link className="btn-link" to="/drill">
                Practise
              </Link>
              <NavLink to="/profile">Profile</NavLink>
              <button
                className="link"
                onClick={async () => {
                  await signOut();
                  navigate("/");
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <NavLink to="/login">Sign in</NavLink>
              <Link className="btn-link" to="/signup">
                Create account
              </Link>
            </>
          )}
        </div>
      </header>

      <main className="page">
        <Outlet />
      </main>

      <footer className="foot">
        <span>
          A practice tool from <a href="https://gazar.dev">gazar.dev</a>
        </span>
        <span className="foot-links">
          <Link to="/support">Support this</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <a href={SPONSOR_URL}>Sponsor</a>
        </span>
      </footer>
    </div>
  );
}
